import { Building2, User, MapPin, Search } from "lucide-react";
import { CompanyAddressDisplay } from "@/components/companies/CompanyAddressDisplay";
import { useTranslation } from "react-i18next";
import { searchBelgianCompany } from "@/utils/company-search";
import { toast } from "@/hooks/use-toast";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

interface CompanyHeaderProps {
  company: {
    id: string;
    name: string;
    street?: string | null;
    postal_code?: string | null;
    city?: string | null;
    country?: string | null;
    address?: string | null;
    assignee?: string | null;
    notes?: string | null;
    logo_url?: string | null;
    vat_number?: string | null;
  };
  refreshCompany?: () => void;
}

export const CompanyHeader = ({ company, refreshCompany }: CompanyHeaderProps) => {
  const { t } = useTranslation();
  const [isSearching, setIsSearching] = useState(false);
  const [isLogoDialogOpen, setIsLogoDialogOpen] = useState(false);
  const [logoResults, setLogoResults] = useState<{ brandId: string; name: string; icon: string; }[]>([]);
  const [selectedLogoUrl, setSelectedLogoUrl] = useState<string | null>(null);

  const searchAndShowLogos = async () => {
    if (!company.name && !company.vat_number) {
      toast({
        title: "Missing Information",
        description: "A company name or VAT number is required to search for a logo.",
        variant: "destructive",
      });
      return;
    }

    setIsSearching(true);
    try {
      // Search using company name or VAT number
      const searchTerm = company.name || company.vat_number;
      const result = await searchBelgianCompany(searchTerm);
      
      if (!result || result.logos.length === 0) {
        toast({
          title: "Logo Not Found",
          description: "No logo was found for this company.",
        });
        return;
      }

      // Open dialog with logo options
      setLogoResults(result.logos);
      setIsLogoDialogOpen(true);
    } catch (error) {
      console.error("Error searching for company logo:", error);
      toast({
        title: "Search Failed",
        description: "Failed to search for company logo.",
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const updateCompanyLogo = async (logoUrl: string) => {
    try {
      const { error } = await supabase
        .from('companies')
        .update({ logo_url: logoUrl })
        .eq('id', company.id);

      if (error) {
        throw error;
      }

      toast({
        title: "Logo Updated",
        description: "Company logo has been updated successfully.",
      });

      // Refresh the company data to show the new logo
      if (refreshCompany) {
        refreshCompany();
      }
    } catch (error) {
      console.error("Error updating company logo:", error);
      toast({
        title: "Update Failed",
        description: "Failed to update company logo.",
        variant: "destructive",
      });
    }
  };

  const confirmLogoSelection = () => {
    if (selectedLogoUrl) {
      updateCompanyLogo(selectedLogoUrl);
      setIsLogoDialogOpen(false);
      setSelectedLogoUrl(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
        <div className="relative h-24 w-24 bg-digger-light-gray rounded-lg flex items-center justify-center">
          {company.logo_url ? (
            <img 
              src={company.logo_url} 
              alt={`${company.name} logo`} 
              className="h-20 w-20 object-contain" 
            />
          ) : (
            <Building2 className="h-12 w-12 text-gray-500" />
          )}
          <Button 
            variant="outline" 
            size="icon" 
            className="absolute -top-2 -right-2 h-8 w-8 rounded-full bg-white" 
            onClick={searchAndShowLogos}
            disabled={isSearching}
            title={t("companies.search.searchForLogo", "Search for company logo")}
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="flex-1">
          <h2 className="text-2xl font-semibold text-center md:text-left mb-4">{company.name}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">{t("companies.info.address")}</h3>
              <CompanyAddressDisplay company={company} />
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">{t("companies.info.assignee")}</h3>
              <div className="flex items-center">
                <User className="h-5 w-5 text-gray-400 mr-3" />
                <span>{company.assignee || "-"}</span>
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">{t("companies.info.notes")}</h3>
              <p className="text-gray-700">{company.notes || "-"}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Logo Search Dialog */}
      <Dialog open={isLogoDialogOpen} onOpenChange={setIsLogoDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("companies.search.selectLogo", "Select Company Logo")}</DialogTitle>
            <DialogDescription>
              {t("companies.search.selectLogoDescription", "Choose a logo for this company")}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-60 my-4">
            <RadioGroup
              value={selectedLogoUrl || ""}
              onValueChange={setSelectedLogoUrl}
              className="grid grid-cols-2 sm:grid-cols-3 gap-3"
            >
              {logoResults.map((logo) => (
                <Label
                  key={logo.brandId}
                  htmlFor={`logo-${logo.brandId}`}
                  className={`relative flex flex-col items-center justify-center p-2 border rounded-md cursor-pointer hover:border-digger-blue ${selectedLogoUrl === logo.icon ? 'border-digger-blue ring-2 ring-digger-blue' : ''}`}
                >
                  <RadioGroupItem value={logo.icon} id={`logo-${logo.brandId}`} className="sr-only" />
                  <img src={logo.icon} alt={`${logo.name} logo`} className="h-12 w-12 object-contain mb-1" />
                  <span className="text-xs text-center truncate w-full">{logo.name}</span>
                </Label>
              ))}
            </RadioGroup>
          </ScrollArea>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setIsLogoDialogOpen(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button onClick={confirmLogoSelection} disabled={!selectedLogoUrl}>
              {t("common.confirm", "Confirm")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
